import { Injectable } from "@angular/core";
import { FetchStatusSelector } from "@obg/ngrx-utils";

import { PlatformName } from "../app-context/app-context.model";
import { AppContextSelector } from "../app-context/app-context.selector";
import { SessionSelector } from "../auth/session.selector";
import { OperatingSystemService } from "../operating-system/operating-system.service";
import { CoreState } from "../state";

import { ACTION_PREFIX } from "./auth-transaction.action";
import { AuthTransactionConfigService } from "./auth-transaction.config";
import { REDIRECT_HASH } from "./auth-transaction.const";
import { VerificationStatus } from "./auth-transaction.model";

@Injectable({
	providedIn: "root",
})
export class AuthTransactionSelector {
	constructor(
		private sessionSelector: SessionSelector,
		private config: AuthTransactionConfigService,
		private operatingSystemService: OperatingSystemService
	) {}

	isBusy() {
		return (state: CoreState): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);
	}

	getLastActivePersonalNumber() {
		return (state: CoreState): string | undefined => state.authTransaction.lastActivePersonalNumber;
	}

	getTransactionId() {
		return (state: CoreState): string | undefined => state.authTransaction.transactionId;
	}

	getLastStatus() {
		return (state: CoreState): VerificationStatus | undefined => state.authTransaction.verificationStatus;
	}

	getVerificationCode() {
		return (state: CoreState): string | undefined => state.authTransaction.verificationCode;
	}

	getAuthenticationToken() {
		return (state: CoreState): string | undefined => state.authTransaction.authenticationToken;
	}

	getBankIdDeepLink() {
		return (state: CoreState): { deepLink: string; isRedirected: boolean } | undefined => {
			const notVerifiedData = this.sessionSelector.getCustomerNotVerifiedData()(state);
			if (!notVerifiedData || !notVerifiedData.autostartToken) {
				return undefined;
			}
			const { transactionId } = notVerifiedData;
			const isIos = this.operatingSystemService.getOperatingSystem() === PlatformName.IOS;
			let redirect = null;
			let isRedirected = false;
			if (AppContextSelector.getDevice(state).isNative) {
				redirect = encodeURIComponent(this.config.getNativeRedirectUrl());
			} else if (transactionId && isIos) {
				const isChromeIos = navigator.userAgent.match("CriOS");
				if (isChromeIos) {
					redirect = "googlechrome://";
				} else {
					redirect = encodeURIComponent(`${window.location.href}#${REDIRECT_HASH}`);
					isRedirected = true;
				}
			}
			return {
				deepLink: `bankid:///?autostarttoken=${notVerifiedData.autostartToken}&redirect=${redirect}`,
				isRedirected,
			};
		};
	}

	getQrCode() {
		return (state: CoreState): string[] | undefined => state.authTransaction.qrCode;
	}
}
